import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Settings2, Plus, ArrowRight, Thermometer, Layers } from 'lucide-react';
import { toast } from 'sonner';

const Print3DEquipment = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrinters = async () => {
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/printing3d/printers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPrinters(res.data || []);
      } catch {
        toast.error('Failed to load printers');
      } finally {
        setLoading(false);
      }
    };
    fetchPrinters();
  }, [token]);

  return (
    <div
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.PRINTING3D_DASHBOARD)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-2">My Equipment</div>
          <h1
            className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            My Printers
          </h1>
          <p className="mt-2 text-sm text-zinc-300">
            Saved printer profiles used by the calculator and troubleshooting tools.
          </p>
        </div>

        {/* Add printer CTA */}
        <Link to="/3d-printing/setup">
          <Button className="bg-orange-500 hover:bg-orange-600 text-black font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add / Configure Printer
          </Button>
        </Link>

        {/* Printer list */}
        {loading ? (
          <div className="text-zinc-400 py-8 text-center">Loading...</div>
        ) : printers.length === 0 ? (
          <Card className="bg-black/60 border-zinc-700">
            <CardContent className="py-10 text-center space-y-4">
              <Printer className="w-12 h-12 text-zinc-600 mx-auto" />
              <h3 className="text-white font-bold text-lg">No 3D Printers Yet</h3>
              <p className="text-zinc-400 text-sm">Add your printer to enable the calculator, slicer guide, and troubleshooting tools.</p>
              <Link to="/3d-printing/setup">
                <Button className="bg-[#c85a00] hover:bg-[#a84800] text-white">
                  Set Up Your First Printer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {printers.map((printer) => (
              <Card key={printer.id} className="bg-black/60 border-zinc-700 hover:border-orange-500/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white flex items-center gap-2 text-lg">
                      <Printer className="w-5 h-5 text-orange-500 flex-shrink-0" />
                      {printer.name || `${printer.brand} ${printer.model}`}
                    </CardTitle>
                    <Link to="/3d-printing/setup">
                      <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300 hover:border-orange-500 hover:text-orange-400">
                        <Settings2 className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                    </Link>
                  </div>
                  {printer.brand && printer.model && (
                    <p className="text-sm text-zinc-400">{printer.brand} · {printer.model}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {printer.nozzle_diameter && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        <Layers className="w-3 h-3 mr-1" />
                        {printer.nozzle_diameter}mm nozzle
                      </Badge>
                    )}
                    {printer.direct_drive !== undefined && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        {printer.direct_drive ? 'Direct Drive' : 'Bowden'}
                      </Badge>
                    )}
                    {printer.enclosure !== undefined && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        {printer.enclosure ? 'Enclosed' : 'Open Frame'}
                      </Badge>
                    )}
                    {printer.max_nozzle_temp && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        <Thermometer className="w-3 h-3 mr-1" />
                        Max {printer.max_nozzle_temp}°C nozzle
                      </Badge>
                    )}
                    {printer.max_bed_temp && (
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">
                        <Thermometer className="w-3 h-3 mr-1" />
                        Max {printer.max_bed_temp}°C bed
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Print3DEquipment;
